// Facade de busca de mensagens do chat (client-side).
// Reusa messageStore.search (match normalizado, dedupe, filtro por conversa,
// ordenação DESC e cap de 100) e adiciona um snippet com o termo destacável
// para a UI renderizar (ex.: envolver em <mark>).

import { dobrar as foldPerChar } from './dobrarAcento'
import { messageStore, type StoredMessage } from './messageStore'

export interface Snippet {
  /** Janela (~60 chars) do corpo ORIGINAL, centrada no 1º match, com "…" quando truncado. */
  text: string
  /** Índice do match relativo a `text` (após qualquer "…" inicial). -1 se não houver match. */
  matchStart: number
  /** Comprimento do termo casado em `text`. 0 se não houver match. */
  matchLen: number
}

export interface SearchHit {
  id: number
  conversation_id: number
  snippet: Snippet
  created_at: string
}

/** Tamanho alvo da janela do snippet (sem contar as reticências). */
const WINDOW = 60

export function makeSnippet(body: string, query: string): Snippet {
  const q = query.trim()
  if (!body) return { text: '', matchStart: -1, matchLen: 0 }

  const foldedBody = foldPerChar(body)
  const foldedQuery = foldPerChar(q)

  const matchIdx = foldedQuery.length > 0 ? foldedBody.indexOf(foldedQuery) : -1

  // Sem match: devolve o começo do corpo (head).
  if (matchIdx < 0) {
    const head = body.length > WINDOW ? body.slice(0, WINDOW) + '…' : body
    return { text: head, matchStart: -1, matchLen: 0 }
  }

  const matchLen = foldedQuery.length

  // Centra a janela no match.
  const half = Math.floor((WINDOW - matchLen) / 2)
  let start = matchIdx - half
  let end = matchIdx + matchLen + (WINDOW - matchLen - half)

  // Ajusta para os limites do corpo, redistribuindo o excedente.
  if (start < 0) {
    end += -start
    start = 0
  }
  if (end > body.length) {
    start -= end - body.length
    end = body.length
    if (start < 0) start = 0
  }

  const leading = start > 0
  const trailing = end < body.length

  let text = body.slice(start, end)
  let matchStart = matchIdx - start
  if (leading) {
    text = '…' + text
    matchStart += 1 // o "…" empurra o match uma posição à frente
  }
  if (trailing) {
    text = text + '…'
  }

  return { text, matchStart, matchLen }
}

export async function searchMessages(
  query: string,
  opts?: { conversationId?: number },
): Promise<SearchHit[]> {
  const rows: StoredMessage[] = await messageStore.search(query, opts)
  return rows.map((m) => ({
    id: m.id,
    conversation_id: m.conversation_id,
    created_at: m.created_at,
    snippet: makeSnippet(m.body ?? '', query),
  }))
}
